'use client'

import { fetchPostalCode } from '@/lib/api/fetch-postal-code'
import {
    createSupplier,
    getSupplier,
    updateSupplier,
} from '@/lib/api/suppliers/supplier-api'
import {
    createSupplierType,
    getSupplierTypesByOrganization,
    SupplierType,
} from '@/lib/api/suppliers/supplier-type-api'
import {
    PayoutScheduleSection,
    PayoutTransferValue,
    PayoutScheduleValue,
    payoutScheduleToPayload,
    payoutTransferToPayload,
    validatePayoutScheduleClient,
} from '@/components/payout-schedule/PayoutScheduleSection'
import { mask, unMask, formatPhoneNumber } from '@/lib/form-state/util'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdBusiness,
    MdContactPhone,
    MdLocalShipping,
    MdLocationOn,
    MdNotes,
    MdSave,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface SupplierFormData {
    name: string
    tradingName: string
    cnpj: string
    stateRegistration: string
    typeId: number | null
    email: string
    phone: string
    website: string
    street: string
    number: string
    complement: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
    contactName: string
    contactPhone: string
    contactEmail: string
    notes: string
    active: boolean
}

const INITIAL_FORM: SupplierFormData = {
    name: '',
    tradingName: '',
    cnpj: '',
    stateRegistration: '',
    typeId: null,
    email: '',
    phone: '',
    website: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    notes: '',
    active: true,
}

const formatCnpj = (value: string): string => {
    const numbers = unMask(value).slice(0, 14)
    if (numbers.length <= 2) return numbers
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12)}`
}

const formatCep = (value: string): string => {
    const digits = unMask(value)
    return mask(digits, '99999-999')
}

const TABS = [
    { key: 'basic', label: 'Informações Básicas', icon: MdBusiness },
    { key: 'address', label: 'Endereço', icon: MdLocationOn },
    { key: 'contact', label: 'Contato', icon: MdContactPhone },
    { key: 'notes', label: 'Observações', icon: MdNotes },
] as const

type TabKey = (typeof TABS)[number]['key']

export default function SupplierFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEdit = !!id

    const [form, setForm] = useState<SupplierFormData>(INITIAL_FORM)
    const [payoutSchedule, setPayoutSchedule] = useState<PayoutScheduleValue>({
        payoutScheduleType: 'OPEN',
        payoutFixedDay: '',
        payoutDaysAfterAppointment: '',
    })
    /** Dados para transferência do repasse (PIX ou banco). */
    const [payoutTransfer, setPayoutTransfer] = useState<PayoutTransferValue>({
        pixKey: '',
        pixKeyType: '',
        bankCode: '',
        bankAgency: '',
        bankAccount: '',
        bankAccountDigit: '',
        bankAccountType: 'CONTA_CORRENTE',
    })
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [activeTab, setActiveTab] = useState<TabKey>('basic')
    const [loadingCep, setLoadingCep] = useState(false)
    const [supplierTypes, setSupplierTypes] = useState<SupplierType[]>([])
    const [typeInput, setTypeInput] = useState('')
    const [showTypeDropdown, setShowTypeDropdown] = useState(false)

    useEffect(() => {
        if (user?.organizationId) {
            getSupplierTypesByOrganization(user.organizationId).then(res => {
                if (res.success) setSupplierTypes(res.result)
            })
        }
    }, [user?.organizationId])

    useEffect(() => {
        if (isEdit && id) {
            loadSupplier(parseInt(id, 10))
        }
    }, [id])

    const loadSupplier = async (supplierId: number) => {
        setLoading(true)
        try {
            const response = await getSupplier(supplierId)
            if (response.success && response.result) {
                const s = response.result
                setForm({
                    name: s.name || '',
                    tradingName: s.tradingName || '',
                    cnpj: s.cnpj ? formatCnpj(s.cnpj) : '',
                    stateRegistration: s.stateRegistration || '',
                    typeId: (s as any).typeId || null,
                    email: s.email || '',
                    phone: s.phone ? formatPhoneNumber(s.phone) : '',
                    website: s.website || '',
                    street: s.street || '',
                    number: s.number || '',
                    complement: s.complement || '',
                    neighborhood: s.neighborhood || '',
                    city: s.city || '',
                    state: s.state || '',
                    zipCode: s.zipCode ? formatCep(s.zipCode) : '',
                    contactName: s.contactName || '',
                    contactPhone: s.contactPhone ? formatPhoneNumber(s.contactPhone) : '',
                    contactEmail: s.contactEmail || '',
                    notes: s.notes || '',
                    active: s.active,
                })
                if ((s as any).type?.name) {
                    setTypeInput((s as any).type.name)
                }
                setPayoutSchedule({
                    payoutScheduleType: (s as any).payoutScheduleType || 'OPEN',
                    payoutFixedDay: (s as any).payoutFixedDay
                        ? String((s as any).payoutFixedDay)
                        : '',
                    payoutDaysAfterAppointment: (s as any).payoutDaysAfterAppointment
                        ? String((s as any).payoutDaysAfterAppointment)
                        : '',
                })
                setPayoutTransfer({
                    pixKey: (s as any).pixKey || '',
                    pixKeyType: (s as any).pixKeyType || '',
                    bankCode: (s as any).bankCode || '',
                    bankAgency: (s as any).bankAgency || '',
                    bankAccount: (s as any).bankAccount || '',
                    bankAccountDigit: (s as any).bankAccountDigit || '',
                    bankAccountType:
                        (s as any).bankAccountType || 'CONTA_CORRENTE',
                })
            } else {
                toast.error('Fornecedor não encontrado')
                navigate('/person/suppliers')
            }
        } catch {
            toast.error('Erro ao carregar fornecedor')
            navigate('/person/suppliers')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (
        field: keyof SupplierFormData,
        value: string | boolean,
    ) => {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    const handleZipCodeChange = async (value: string) => {
        const formatted = formatCep(value)
        handleChange('zipCode', formatted)

        const cleanZip = unMask(value)
        if (cleanZip.length === 8) {
            setLoadingCep(true)
            try {
                const data = await fetchPostalCode(cleanZip)
                if (data && !data.erro) {
                    setForm(prev => ({
                        ...prev,
                        street: data.logradouro || prev.street,
                        neighborhood: data.bairro || prev.neighborhood,
                        city: data.localidade || prev.city,
                        state: data.uf || prev.state,
                        complement: data.complemento || prev.complement,
                    }))
                }
            } catch {
                // CEP não encontrado - não exibe erro
            } finally {
                setLoadingCep(false)
            }
        }
    }

    const validate = (): boolean => {
        if (!form.name.trim()) {
            toast.error('Nome do fornecedor é obrigatório')
            setActiveTab('basic')
            return false
        }
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error('Email inválido')
            setActiveTab('basic')
            return false
        }
        if (
            form.contactEmail &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)
        ) {
            toast.error('Email do contato inválido')
            setActiveTab('contact')
            return false
        }
        const payoutError = validatePayoutScheduleClient(payoutSchedule)
        if (payoutError) {
            toast.warning(payoutError)
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) return
        if (!user?.organizationId) return

        setSaving(true)
        try {
            const data: any = {
                name: form.name.trim(),
                active: form.active,
                organizationId: user.organizationId,
            }

            // Tipo de fornecedor
            if (form.typeId) data.typeId = form.typeId
            else data.typeId = null

            // Somente enviar campos preenchidos
            if (form.tradingName.trim()) data.tradingName = form.tradingName.trim()
            const cnpjClean = unMask(form.cnpj)
            if (cnpjClean) data.cnpj = cnpjClean
            if (form.stateRegistration.trim()) data.stateRegistration = form.stateRegistration.trim()
            if (form.email.trim()) data.email = form.email.trim()
            const phoneClean = unMask(form.phone)
            if (phoneClean) data.phone = phoneClean
            if (form.website.trim()) data.website = form.website.trim()
            if (form.street.trim()) data.street = form.street.trim()
            if (form.number.trim()) data.number = form.number.trim()
            if (form.complement.trim()) data.complement = form.complement.trim()
            if (form.neighborhood.trim()) data.neighborhood = form.neighborhood.trim()
            if (form.city.trim()) data.city = form.city.trim()
            if (form.state.trim()) data.state = form.state.trim()
            const zipClean = unMask(form.zipCode)
            if (zipClean) data.zipCode = zipClean
            if (form.contactName.trim()) data.contactName = form.contactName.trim()
            const contactPhoneClean = unMask(form.contactPhone)
            if (contactPhoneClean) data.contactPhone = contactPhoneClean
            if (form.contactEmail.trim()) data.contactEmail = form.contactEmail.trim()
            if (form.notes.trim()) data.notes = form.notes.trim()

            Object.assign(
                data,
                payoutScheduleToPayload(payoutSchedule),
                payoutTransferToPayload(payoutTransfer),
            )

            let response
            if (isEdit && id) {
                response = await updateSupplier(parseInt(id, 10), data)
            } else {
                response = await createSupplier(data)
            }

            if (response.success) {
                toast.success(
                    isEdit
                        ? 'Fornecedor atualizado com sucesso'
                        : 'Fornecedor criado com sucesso',
                )
                navigate('/person/suppliers')
            } else {
                toast.error(
                    response.error ||
                    (isEdit
                        ? 'Erro ao atualizar fornecedor'
                        : 'Erro ao criar fornecedor'),
                )
            }
        } catch {
            toast.error(
                isEdit
                    ? 'Erro ao atualizar fornecedor'
                    : 'Erro ao criar fornecedor',
            )
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando fornecedor...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/person/suppliers')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdLocalShipping className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEdit ? 'Editar Fornecedor' : 'Novo Fornecedor'}
                        </h1>
                        <p className="text-sm text-base-content/60 mt-1">
                            {isEdit
                                ? 'Atualize os dados do fornecedor'
                                : 'Preencha os dados do novo fornecedor'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div
                role="tablist"
                className="tabs tabs-bordered tabs-lg overflow-x-auto"
            >
                {TABS.map(tab => {
                    const TabIcon = tab.icon
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            className={`tab whitespace-nowrap !rounded-none gap-2 ${activeTab === tab.key ? 'tab-active border-b-2 border-b-primary' : ''
                                }`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <TabIcon className="text-base" />
                            {tab.label}
                        </button>
                    )
                })}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                    {/* Tab: Informações Básicas */}
                    {activeTab === 'basic' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Razão Social *
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome do fornecedor"
                                    className="input input-bordered w-full"
                                    value={form.name}
                                    onChange={e =>
                                        handleChange('name', e.target.value)
                                    }
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome Fantasia
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome fantasia"
                                    className="input input-bordered w-full"
                                    value={form.tradingName}
                                    onChange={e =>
                                        handleChange(
                                            'tradingName',
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        CNPJ
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="00.000.000/0000-00"
                                    className="input input-bordered w-full"
                                    value={form.cnpj}
                                    onChange={e =>
                                        handleChange('cnpj', formatCnpj(e.target.value))
                                    }
                                    maxLength={18}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Inscrição Estadual
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Inscrição estadual"
                                    className="input input-bordered w-full"
                                    value={form.stateRegistration}
                                    onChange={e =>
                                        handleChange(
                                            'stateRegistration',
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full relative">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Tipo de Fornecedor
                                    </span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Digite para buscar ou criar um tipo..."
                                        value={typeInput}
                                        onChange={e => {
                                            setTypeInput(e.target.value)
                                            setShowTypeDropdown(true)
                                            if (!e.target.value.trim()) {
                                                setForm(prev => ({ ...prev, typeId: null }))
                                            }
                                        }}
                                        onFocus={() => setShowTypeDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowTypeDropdown(false), 200)}
                                    />
                                    {typeInput && (
                                        <button
                                            type="button"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs"
                                            onClick={() => {
                                                setTypeInput('')
                                                setForm(prev => ({ ...prev, typeId: null }))
                                            }}
                                        >
                                            x
                                        </button>
                                    )}
                                </div>
                                {showTypeDropdown && typeInput.trim() && (
                                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {supplierTypes
                                            .filter(t => t.name.toLowerCase().includes(typeInput.toLowerCase().trim()))
                                            .map(type => (
                                                <button
                                                    key={type.id}
                                                    type="button"
                                                    className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm"
                                                    onMouseDown={() => {
                                                        setForm(prev => ({ ...prev, typeId: type.id }))
                                                        setTypeInput(type.name)
                                                        setShowTypeDropdown(false)
                                                    }}
                                                >
                                                    {type.name}
                                                </button>
                                            ))}
                                        {!supplierTypes.some(t => t.name.toLowerCase() === typeInput.toLowerCase().trim()) && typeInput.trim() && (
                                            <button
                                                type="button"
                                                className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm text-secondary flex items-center gap-2 border-t border-base-300/50"
                                                onMouseDown={async () => {
                                                    if (!user?.organizationId) return
                                                    try {
                                                        const response = await createSupplierType({
                                                            name: typeInput.trim(),
                                                            organizationId: user.organizationId,
                                                        })
                                                        if (response.success) {
                                                            const newType = response.result
                                                            setSupplierTypes(prev => [...prev, newType].sort((a, b) => a.name.localeCompare(b.name)))
                                                            setForm(prev => ({ ...prev, typeId: newType.id }))
                                                            setTypeInput(newType.name)
                                                            setShowTypeDropdown(false)
                                                            toast.success('Tipo criado com sucesso')
                                                        } else {
                                                            toast.error(response.error || 'Erro ao criar tipo')
                                                        }
                                                    } catch {
                                                        toast.error('Erro ao criar tipo')
                                                    }
                                                }}
                                            >
                                                <MdAdd className="text-lg" />
                                                Criar tipo: &quot;{typeInput.trim()}&quot;
                                            </button>
                                        )}
                                        {supplierTypes.filter(t => t.name.toLowerCase().includes(typeInput.toLowerCase().trim())).length === 0 &&
                                            supplierTypes.some(t => t.name.toLowerCase() === typeInput.toLowerCase().trim()) && (
                                                <div className="px-4 py-2 text-sm text-base-content/50">
                                                    Nenhum resultado
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Email
                                    </span>
                                </label>
                                <input
                                    type="email"
                                    placeholder="email@fornecedor.com"
                                    className="input input-bordered w-full"
                                    value={form.email}
                                    onChange={e =>
                                        handleChange('email', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Telefone
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="(00) 00000-0000"
                                    className="input input-bordered w-full"
                                    value={form.phone}
                                    onChange={e =>
                                        handleChange('phone', formatPhoneNumber(e.target.value))
                                    }
                                    maxLength={15}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Website
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="https://www.fornecedor.com"
                                    className="input input-bordered w-full"
                                    value={form.website}
                                    onChange={e =>
                                        handleChange('website', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={form.active}
                                        onChange={e =>
                                            handleChange(
                                                'active',
                                                e.target.checked,
                                            )
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Fornecedor Ativo
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Tab: Endereço */}
                    {activeTab === 'address' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        CEP
                                    </span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="00000-000"
                                        className="input input-bordered w-full"
                                        value={form.zipCode}
                                        onChange={e =>
                                            handleZipCodeChange(e.target.value)
                                        }
                                    />
                                    {loadingCep && (
                                        <span className="loading loading-spinner loading-sm absolute right-3 top-1/2 -translate-y-1/2 text-secondary" />
                                    )}
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Rua
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome da rua"
                                    className="input input-bordered w-full"
                                    value={form.street}
                                    onChange={e =>
                                        handleChange('street', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Número
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Número"
                                    className="input input-bordered w-full"
                                    value={form.number}
                                    onChange={e =>
                                        handleChange('number', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Complemento
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Complemento"
                                    className="input input-bordered w-full"
                                    value={form.complement}
                                    onChange={e =>
                                        handleChange(
                                            'complement',
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Bairro
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Bairro"
                                    className="input input-bordered w-full"
                                    value={form.neighborhood}
                                    onChange={e =>
                                        handleChange(
                                            'neighborhood',
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Cidade
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Cidade"
                                    className="input input-bordered w-full"
                                    value={form.city}
                                    onChange={e =>
                                        handleChange('city', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Estado
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.state}
                                    onChange={e =>
                                        handleChange('state', e.target.value)
                                    }
                                >
                                    <option value="">Selecione</option>
                                    {[
                                        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES',
                                        'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR',
                                        'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
                                        'SP', 'SE', 'TO',
                                    ].map(uf => (
                                        <option key={uf} value={uf}>
                                            {uf}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Tab: Contato */}
                    {activeTab === 'contact' && (
                        <div className="space-y-6">
                            <div className="bg-base-200/30 rounded-xl p-4 mb-4">
                                <p className="text-sm text-base-content/70">
                                    Dados da pessoa de contato principal do
                                    fornecedor.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Nome do Contato
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Nome da pessoa de contato"
                                        className="input input-bordered w-full"
                                        value={form.contactName}
                                        onChange={e =>
                                            handleChange(
                                                'contactName',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Telefone do Contato
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="(00) 00000-0000"
                                        className="input input-bordered w-full"
                                        value={form.contactPhone}
                                        onChange={e =>
                                            handleChange(
                                                'contactPhone',
                                                formatPhoneNumber(e.target.value),
                                            )
                                        }
                                        maxLength={15}
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Email do Contato
                                        </span>
                                    </label>
                                    <input
                                        type="email"
                                        placeholder="contato@fornecedor.com"
                                        className="input input-bordered w-full"
                                        value={form.contactEmail}
                                        onChange={e =>
                                            handleChange(
                                                'contactEmail',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab: Observações */}
                    {activeTab === 'notes' && (
                        <div className="space-y-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Observações
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full min-h-[200px]"
                                    placeholder="Observações sobre o fornecedor, condições comerciais, prazos de entrega, etc."
                                    value={form.notes}
                                    onChange={e =>
                                        handleChange('notes', e.target.value)
                                    }
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6">
                    <PayoutScheduleSection
                        value={payoutSchedule}
                        onChange={setPayoutSchedule}
                        description="Defina como os valores serão repassados a este fornecedor."
                        transfer={{
                            value: payoutTransfer,
                            onChange: setPayoutTransfer,
                        }}
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/person/suppliers')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <span className="loading loading-spinner loading-sm" />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                {isEdit ? 'Salvar Alterações' : 'Criar Fornecedor'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
